import { useEffect, useRef } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
  'select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Drawer/modal a11y primitives:
 *   - close on ESC
 *   - focus-trap inside the panel
 *   - restore focus to whatever opened the drawer on unmount
 *
 * Returns a ref to attach to the drawer panel element.
 *
 * Task Mgmt v1.2 FOCUS-FIX: the effect used to depend on `onClose` — callers
 * passing an inline `() => onClose()` re-ran it on EVERY render (every
 * keystroke → setState → render), and the rAF initial-focus re-grabbed the
 * first focusable (the × close button) — inputs lost focus per keypress.
 * `onClose` now lives in a ref (latest-callback pattern) and the effect runs
 * ONCE per open. Initial focus also prefers the first FIELD over buttons.
 */
export function useDrawerA11y(open: boolean, onClose: () => void) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Latest-callback ref — keeps ESC working with fresh closures WITHOUT
  // re-running the focus effect on every render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Initial focus — prefer the first FIELD (input/select/textarea) so the
    // user can type immediately; fall back to first focusable, then the panel.
    const setInitialFocus = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const firstField = panel.querySelector<HTMLElement>(
        'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])',
      );
      if (firstField) {
        firstField.focus();
        return;
      }
      const focusable = panel.querySelectorAll<HTMLElement>(FOCUSABLE);
      const first = focusable[0];
      if (first) first.focus();
      else panel.focus();
    };
    const raf = requestAnimationFrame(setInitialFocus);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !el.hasAttribute("disabled") && el.offsetParent !== null,
      );
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    // Lock body scroll while drawer is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
    // ONCE per open — never re-run on re-render (the per-keystroke focus-loss bug).
  }, [open]);

  return panelRef;
}
